import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { competitionAPI } from '../../api/endpoints';
import CompetitionCard from './CompetitionCard';
import LoadingSpinner from '../common/LoadingSpinner';
import {
  FaPlus, FaSearch, FaFilter, FaTimes,
  FaTrophy, FaGift, FaUserCheck,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import './CompetitionList.css';

const CompetitionList = () => {
  const { isAuthenticated, user } = useAuth();

  // ---------- ALL COMPETITIONS ----------
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, current: 1 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    visibility: '',
    sort: 'recent',
  });
  const [showFilters, setShowFilters] = useState(false);

  // ---------- MY COMPETITIONS ----------
  const [myCompetitions, setMyCompetitions] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState(null);

  // ---------- REWARDS ----------
  const [rewards, setRewards] = useState({ total_unclaimed: 0, items: [] });
  const [rewardsLoading, setRewardsLoading] = useState(false);

  // ---------- TAB ----------
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'mine' | 'rewards'

  // ==================== FETCH ALL ====================
  const fetchCompetitions = async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 20,
        page: pagination.current,
        search: filters.search || undefined,
        status: filters.status || undefined,
      };
      const response = await competitionAPI.getList(params);
      setCompetitions(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 0,
        current: pagination.current,
      });
    } catch (error) {
      console.error('Failed to fetch competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetitions();
  }, [pagination.current, filters]);

  // ==================== FETCH MY COMPETITIONS ====================
  const fetchMyCompetitions = useCallback(async (showLoader = false) => {
    if (!isAuthenticated) {
      setMyError('Please log in to see your competitions.');
      return;
    }
    if (showLoader) setMyLoading(true);
    setMyError(null);
    try {
      const res = await competitionAPI.getJoined();

      let data = res?.data;

      // Strip accidental HTML prefix (WP DB errors)
      if (typeof data === 'string') {
        const jsonStart = data.indexOf('[');
        if (jsonStart === -1) throw new Error('Invalid response from server');
        data = JSON.parse(data.slice(jsonStart));
      }

      // Shape might be array OR { data: [...] }
      let items = Array.isArray(data) ? data :
                  Array.isArray(data?.data) ? data.data :
                  [];

      // Sort: active/open first, then by created_at desc
      items.sort((a, b) => {
        const order = { open: 0, active: 0, upcoming: 1, full: 2, completed: 3, ended: 3 };
        const oa = order[a.status] ?? 4;
        const ob = order[b.status] ?? 4;
        if (oa !== ob) return oa - ob;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });

      setMyCompetitions(items);
    } catch (err) {
      console.error('[CompetitionList] my competitions fetch failed:', err);
      const status = err?.response?.status;
      setMyError(
        status === 401 ? 'Session expired — please log in again.' :
        status === 404 ? 'Endpoint not found on the server.' :
        err?.response?.data?.message || 'Failed to load your competitions.'
      );
      setMyCompetitions([]);
    } finally {
      if (showLoader) setMyLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'mine') fetchMyCompetitions(true);
  }, [activeTab, fetchMyCompetitions]);

  // Auto-refresh My Competitions every 30s while tab is visible
  useEffect(() => {
    if (activeTab !== 'mine') return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') fetchMyCompetitions(false);
    }, 30000);
    return () => clearInterval(t);
  }, [activeTab, fetchMyCompetitions]);

  // ==================== FETCH REWARDS ====================
  const fetchRewards = useCallback(async () => {
    if (!isAuthenticated) return;
    setRewardsLoading(true);
    try {
      const res = await competitionAPI.getMyRewards();
      setRewards({
        total_unclaimed: Number(res.data?.total_unclaimed || 0),
        items: Array.isArray(res.data?.items) ? res.data.items : [],
      });
    } catch (err) {
      console.error('[CompetitionList] rewards fetch failed:', err);
    } finally {
      setRewardsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'rewards') fetchRewards();
  }, [activeTab, fetchRewards]);

  // ==================== FILTER HANDLERS ====================
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchCompetitions();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '', visibility: '', sort: 'recent' });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  const handleClaimReward = async (competitionId) => {
    try {
      const res = await competitionAPI.claimReward(competitionId);
      if (res.data?.success) {
        toast.success(`Claimed ${Number(res.data.amount).toFixed(2)} $XPUNT`);
        await fetchRewards();
        await fetchMyCompetitions(false);
      } else {
        toast.error(res.data?.message || 'Claim failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Claim failed');
    }
  };

  // ==================== LOADING GUARD ====================
  if (loading && pagination.current === 1 && activeTab === 'all') {
    return (
      <div className="competition-list-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="competition-list-container">
      <div className="competition-list-header">
        <div>
          <h2>Competitions</h2>
          <p className="subtitle">Join competitions and test your prediction skills</p>
        </div>
        {isAuthenticated && user?.punter_plus && (
          <Link to="/competitions/create" className="btn btn-primary">
            <FaPlus /> Create Competition
          </Link>
        )}
      </div>

      {/* ==================== TABS ==================== */}
      <div className="competition-tabs">
        <button
          className={`competition-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
          type="button"
        >
          <FaTrophy /> All Competitions
        </button>
        <button
          className={`competition-tab ${activeTab === 'mine' ? 'active' : ''}`}
          onClick={() => setActiveTab('mine')}
          type="button"
        >
          <FaUserCheck /> My Competitions
          {myCompetitions.length > 0 && (
            <span className="tab-count">{myCompetitions.length}</span>
          )}
        </button>
        <button
          className={`competition-tab ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
          type="button"
        >
          <FaGift /> My Rewards
          {rewards.total_unclaimed > 0 && (
            <span className="tab-badge">
              {rewards.total_unclaimed.toFixed(2)} $XPUNT
            </span>
          )}
        </button>
      </div>

      {/* ==================== TAB: ALL COMPETITIONS ==================== */}
      {activeTab === 'all' && (
        <>
          {/* Search + Filters */}
          <div className="competition-search">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search competitions..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="search-input"
                />
              </div>
              <button
                type="button"
                className="filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter /> Filters
              </button>
              <button type="submit" className="btn btn-primary">Search</button>
            </form>

            {showFilters && (
              <div className="filters-panel">
                <div className="filter-group">
                  <label>Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="open">Open</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="ended">Ended</option>
                    <option value="full">Full</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Visibility</label>
                  <select
                    value={filters.visibility}
                    onChange={(e) => handleFilterChange('visibility', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>
                <button onClick={clearFilters} className="btn btn-secondary btn-sm">
                  <FaTimes /> Clear
                </button>
              </div>
            )}
          </div>

          {/* Results Count */}
          <div className="results-info">
            <span>{pagination.total} competitions found</span>
          </div>

          {/* Grid */}
          {competitions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏆</div>
              <h3>No competitions found</h3>
              <p>Try adjusting your search or filters</p>
              {isAuthenticated && user?.punter_plus && (
                <Link to="/competitions/create" className="btn btn-primary">
                  Create the first competition
                </Link>
              )}
            </div>
          ) : (
            <div className="competition-grid">
              {competitions.map((comp) => (
                <CompetitionCard key={comp.id} competition={comp} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.current - 1)}
                disabled={pagination.current === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <div className="pagination-pages">
                {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                  let pageNum;
                  if (pagination.pages <= 5) pageNum = i + 1;
                  else if (pagination.current <= 3) pageNum = i + 1;
                  else if (pagination.current >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
                  else pageNum = pagination.current - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-page ${pagination.current === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(pagination.current + 1)}
                disabled={pagination.current === pagination.pages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* ==================== TAB: MY COMPETITIONS ==================== */}
      {activeTab === 'mine' && (
        <>
          {myLoading ? (
            <div className="competition-list-loading">
              <LoadingSpinner size="large" />
            </div>
          ) : myError ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <h3>Couldn't load your competitions</h3>
              <p>{myError}</p>
              <button
                className="btn btn-primary"
                onClick={() => fetchMyCompetitions(true)}
                type="button"
              >
                Try Again
              </button>
            </div>
          ) : myCompetitions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏆</div>
              <h3>You haven't joined any competitions yet</h3>
              <p>Browse all competitions and join one to get started</p>
              <button
                className="btn btn-primary"
                onClick={() => setActiveTab('all')}
                type="button"
              >
                Browse Competitions
              </button>
            </div>
          ) : (
            <>
              <div className="results-info">
                <span>{myCompetitions.length} competition{myCompetitions.length !== 1 ? 's' : ''} you've joined</span>
              </div>
              <div className="competition-grid">
                {myCompetitions.map((comp) => (
                  <CompetitionCard key={comp.id} competition={comp} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ==================== TAB: MY REWARDS ==================== */}
      {activeTab === 'rewards' && (
        <>
          {rewardsLoading ? (
            <div className="competition-list-loading">
              <LoadingSpinner size="large" />
            </div>
          ) : rewards.items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏆</div>
              <h3>No rewards yet</h3>
              <p>Win a competition to see your rewards here</p>
            </div>
          ) : (
            <div className="rewards-list">
              {rewards.items.map((item) => (
                <div
                  key={item.competition_id}
                  className={`reward-row ${item.claimed ? 'claimed' : 'unclaimed'}`}
                >
                  <div className="reward-info">
                    <Link
                      to={`/competitions/${item.competition_id}`}
                      className="reward-title"
                    >
                      {item.title}
                    </Link>
                    <span className="reward-rank">Rank #{item.rank}</span>
                  </div>

                  <div className="reward-amount">
                    +{Number(item.amount).toFixed(2)} $XPUNT
                  </div>

                  {item.claimed ? (
                    <span className="reward-claimed-tag">✅ Claimed</span>
                  ) : item.claim_open ? (
                    <button
                      className="reward-claim-btn"
                      onClick={() => handleClaimReward(item.competition_id)}
                      type="button"
                    >
                      Claim
                    </button>
                  ) : (
                    <span className="reward-pending">Claim opens soon</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompetitionList;